import React, { useState, useContext, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SideBar from "../../../../components/common/SideBar/SideBarNew";
import {
  PARENT_FORM_CATEGORY_SLUG,
  USER_ROLE,
  WORKER_PROFILE_TABS,
} from "../../../../Constants/constant";
import { LoginContext } from "../../../../helpers/LoginContext";
import {
  HRM_BREADCRUMB,
  HRM_BREADCRUMB_PAGES,
} from "../../../../Constants/BreadcrumbConstant";
import Breadcrumb from "../../../../components/common/Breadcrumb/Breadcrumb";
import Tabs from "components/common/Enhansment/Tabs/Tabs";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import {
  workerHourlyRateSettingUpdateRequest,
  workerProfileDetailsFetchRequest,
  workerStatusUpdatedRequest,
} from "store/actions/sp.actions";
import { connect, useDispatch } from "react-redux";
import {
  getSelectedWorker,
  getWorkerHourlySettingLoader,
  getWorkerProfileDetailLoader,
  getWorkerProfileError,
  getWorkerStatusUpdateLoader,
  getSpRosteringSetting,
} from "store/selector/sp.selector";
import { getFolderListForHRMLoadingSelector } from "store/selector/cos.selector";
import {
  addFiltersAndPagination,
  apiGetRequest,
} from "../../../../helpers/Requests";
import { roleAccess, canViewAuditLogActivity, canAccessWyzedWorkerTab } from "helpers/RolesPermission";
import {
  BLK_PORTAL_PREMISSION_KEYS_CHILDREN,
  PERMISSION_KEYS,
} from "store/constants";
import ComplianceProgressTooltip from "components/common/ProgressBarNew/ComplianceProgressTooltip";
import { getUrlRole, selectedTabState } from "Utils/utils";
import {
  categoryAppliesToFormModule,
  extractFormCategoriesFromResponse,
  isCustomFormCategory,
} from "components/DynamicForms/shared/formCategoryUtils";
import {
  customHrmFormCategoryTabId,
  isCustomHrmFormCategoryTabId,
  mergeCustomHrmCategoriesIntoFormsTab,
} from "./workerProfileHrmCategoryTabs.utils";
import { MdErrorOutline } from "react-icons/md";
import { trackTabSwitch } from "Utils/trackTabSwitch";
import WorkerProfileHeader from "components/ServiceProvider/Participant/WorkerProfileHeader/WorkerProfileHeader";
import {
  WORKER_COMPLIANCE_SYNC_EVENT,
  WORKER_COMPLIANCE_SYNC_STORAGE_KEY,
  readWorkerComplianceSyncToken,
} from "Utils/workerComplianceSync";

const WorkerProfileDetails = lazy(() => import("./Profile"));
const WorkerDocument = lazy(() => import("./Documents/index"));
const WorkerSetting = lazy(() => import("./WorkerSettings/Setting"));
const SubmittedFormView = lazy(() =>
  import("components/ServiceProvider/SubmittedFormsView/index")
);
const ActivityTab = lazy(() =>
  import("components/common/AuditLog").then((module) => ({
    default: module.ActivityTab,
  }))
);
const WorkerWyzedSection = lazy(() => import("./WorkerWyzedSection"));

const TabContentFallback = () => (
  <div className="flex justify-center items-center py-16">
    <CircularLoader classes="flex justify-center items-center" classloader="loader-l" />
  </div>
);

const WorkerProfile = (props) => {
  const {
    getFolderListForHRMLoading,
    workerHourlyRateSettingRequest,
    workerSettingCreateLoader,
    WorkerProfileDataRequest,
    workerProfileDetailLoader,
    workerProfileError,
    selectedWorker,
    workerStatusUpdateLoader,
    workerStatusUpdated,
    slug,
    spRosteringSetting,
  } = props;

  const dispatch = useDispatch();
  const location = useLocation();
  const { workerId, workerInfo } = useParams();
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);

  const [documentList, setDocumentList] = useState([]);
  const [requiredDocuments, setRequiredDocuments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(WORKER_PROFILE_TABS[0].id);

  const [progressbarData, setProgressBar] = useState([]);
  const userType = localStorage.getItem("userType");
  const [keyStrength, setKeyStrength] = useState([]);
  const [isWorkerAvailablity, setRefreshPageForWorkerTimeSlot] =
    useState(false);
  const [permissionsVersion, setPermissionsVersion] = useState(0);
  const [customHrmFormCategories, setCustomHrmFormCategories] = useState([]);

  useEffect(() => {
    const refreshTabs = () => setPermissionsVersion((v) => v + 1);
    window.addEventListener("refreshPermissions", refreshTabs);
    return () => window.removeEventListener("refreshPermissions", refreshTabs);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadCustomCategories = async () => {
      const prefixes = roleAccess([USER_ROLE.Sp_Standard_User])
        ? ["hrm"]
        : [`${getUrlRole() || "sp"}/hrm`, "hrm"];

      let categories = [];
      for (const rolePrefix of prefixes) {
        try {
          const res = await apiGetRequest(`${rolePrefix}/form-categories`);
          const payload = res?.data?.data ?? res?.data ?? res;
          const list = extractFormCategoriesFromResponse(payload);
          if (list.length > 0) {
            categories = list;
            break;
          }
        } catch {
          /* try next prefix */
        }
      }

      if (cancelled) {
        return;
      }

      setCustomHrmFormCategories(
        categories.filter(
          (category) =>
            isCustomFormCategory(category) &&
            categoryAppliesToFormModule(category, "hrm")
        )
      );
    };
    loadCustomCategories();
    return () => {
      cancelled = true;
    };
  }, [selectedWorker?.uuid]);

  const workerTabsVisible = useMemo(() => {
    let tabs = WORKER_PROFILE_TABS;
    if (!canViewAuditLogActivity(spRosteringSetting)) {
      tabs = tabs.filter((t) => t.id !== 20);
    }
    if (!canAccessWyzedWorkerTab()) {
      tabs = tabs.filter((t) => t.id !== 21);
    }
    return mergeCustomHrmCategoriesIntoFormsTab(tabs, customHrmFormCategories);
  }, [spRosteringSetting, permissionsVersion, customHrmFormCategories]);

  const customHrmCategoryByTabId = useMemo(() => {
    const map = new Map();
    customHrmFormCategories.forEach((category) => {
      map.set(customHrmFormCategoryTabId(category.id), category);
    });
    return map;
  }, [customHrmFormCategories]);

  const activeFormTab = useMemo(() => {
    const formsTab = workerTabsVisible.find((tab) => tab.id === 4);
    return formsTab?.children?.find((child) => child.id === selectedTab) ?? null;
  }, [workerTabsVisible, selectedTab]);

  useEffect(() => {
    if (!canViewAuditLogActivity(spRosteringSetting) && selectedTab === 20) {
      setSelectedTab(1);
    }
    if (!canAccessWyzedWorkerTab() && selectedTab === 21) {
      setSelectedTab(1);
    }
  }, [spRosteringSetting, selectedTab]);

useEffect(()=>{
if(selectedTabState?.get()){
  setSelectedTab(selectedTabState?.get().id)
}
},[])

  useEffect(() => {
    localStorage.setItem("workerTab", selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    if (selectedWorker) {
      localStorage.setItem("selectedWorkerID", selectedWorker.id);
    }
  }, [selectedWorker]);

  useEffect(() => {
    if (location.state?.id != null) {
      setSelectedTab(location.state.id);
    }
  }, [location.state]);

  useEffect(() => {
    const { state } = location;
    if ((!state || state === null) && !workerId) {
      navigate("/all-workers");
    } else if ((state && !state.hasOwnProperty("workerUUID")) || !workerId) {
      navigate("/all-workers");
    } else if (
      (state &&
        state.hasOwnProperty("workerUUID") &&
        state.workerUUID.length > 0) ||
      workerId
    ) {
      WorkerProfileDataRequest({
        profile_id: state && state?.workerUUID ? state?.workerUUID : workerId,
      });
    }
  }, [location.pathname, isWorkerAvailablity]);

  useEffect(() => {
    localStorage.setItem("workerTab", selectedTab);
  }, [selectedTab]);

  useEffect(() => {
    getKeyStrengthData();
  }, []);

  const getKeyStrengthData = useCallback(async () => {
    const res = await apiGetRequest("/hrm/key-strength");
    if (res && res.status === 200) {
      setKeyStrength(res.data.data.key_strengths);
    }
  }, []);

  useEffect(() => {
    if (selectedWorker) {
      localStorage.setItem("selectedWorkerID", selectedWorker.id);
    }
  }, [selectedWorker]);

  const navigate = useNavigate();

  const [child, setChildId] = useState(null);
  const handleTab = (tab) => {
    const fromTab = selectedTab;
    const toTab = tab;
    setSelectedTab(tab);
    try {
      trackTabSwitch("worker", fromTab, toTab);
    } catch (e) {}
  };

  useEffect(() => {
    if (child) {
      localStorage.setItem("workerTab", child);

      setSelectedTab(child);
    }
    if (child === null) {
      const { state } = location;
      if (state && state.hasOwnProperty("id")) {
        setSelectedTab(state.id);
      }
    }
  }, [child]);

  useEffect(() => {
    if (
      roleAccess(
        [],
        PERMISSION_KEYS.DOCUMENT,
        BLK_PORTAL_PREMISSION_KEYS_CHILDREN.DOCUMEMT.SHOW
      )
    ) {
      getEmployeeDocuments();
    }
  }, [selectedWorker?.uuid, userType]);

  const [openRequiredModalAgain, setOpenRequiredModalAgain] = useState(false);

  const getEmployeeDocuments = async (callFrom = null) => {
    try {
      if (!selectedWorker?.uuid) return;

      setIsLoading(true);
      let url = "";
      if (userType === "sp_standard_user") {
        url = addFiltersAndPagination(`/hrm/employee-docs`, {
          page: 1,
          per_page: 100,
        });
      } else {
        url = addFiltersAndPagination(`/sp/employee-docs`, {
          page: 1,
          per_page: 100,
        });
      }
      const response = await apiGetRequest(
        `${url}&profile_id=${selectedWorker?.uuid}`
      );
      const uploadedDoc = response?.data?.data?.employee_documents.data;
      setDocuments(uploadedDoc);
      if (!roleAccess([USER_ROLE.Sp_Standard_User])) {
        getDocumentsListForProgressBar();
      }
      if (callFrom && callFrom === "from-uploaded") {
        setOpenRequiredModalAgain(true);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setIsLoading(false);
      if (
        callFrom &&
        callFrom === "from-uploaded" &&
        openRequiredModalAgain === false
      ) {
        setOpenRequiredModalAgain(true);
      }
    }
  };

  const [isFetchDocumentBarLoader, setIsFetchDocumentBarLoader] =
    useState(false);
  //to get documents list
  const getDocumentsListForProgressBar = async () => {
    try {
      setIsFetchDocumentBarLoader(true);
      const res = await apiGetRequest(
        `/hrm/employee/documents-list?employee_profile_uuid=${selectedWorker?.uuid}`
      );
      if (res && res.status === 200) {
        const workerComplianceSettings = res.data.data[0];
        const requiredDocs = res.data.data[1];
        setDocumentList(requiredDocs);
   
        // Filter out deleted form templates (forms without slug are considered deleted)
        const filteredComplianceSettings = workerComplianceSettings.filter((item) => {
          if (item.type === 'form') {
            // Only include forms that have a slug (template exists and is not deleted)
            return item.slug !== null && item.slug !== undefined;
          }
          // Keep all documents
          return true;
        });

        setRequiredDocuments(filteredComplianceSettings);
        setProgressBar(filteredComplianceSettings);
      }
      setIsFetchDocumentBarLoader(false);
    } catch (error) {
      setIsFetchDocumentBarLoader(false);
      console.log({ error });
    }
  };

  // Issue/expiry date requirements are edited in Worker Compliance settings, which can be
  // another page or another browser tab, so pick the change up without a page reload.
  const complianceSyncTokenRef = useRef(readWorkerComplianceSyncToken());

  useEffect(() => {
    if (!selectedWorker?.uuid || roleAccess([USER_ROLE.Sp_Standard_User])) {
      return undefined;
    }

    const refresh = () => {
      complianceSyncTokenRef.current = readWorkerComplianceSyncToken();
      getDocumentsListForProgressBar();
    };

    const refreshIfChanged = () => {
      if (readWorkerComplianceSyncToken() === complianceSyncTokenRef.current) {
        return;
      }
      refresh();
    };

    const handleStorage = (e) => {
      if (e.key && e.key !== WORKER_COMPLIANCE_SYNC_STORAGE_KEY) return;
      refreshIfChanged();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) refreshIfChanged();
    };

    window.addEventListener(WORKER_COMPLIANCE_SYNC_EVENT, refresh);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", refreshIfChanged);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener(WORKER_COMPLIANCE_SYNC_EVENT, refresh);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", refreshIfChanged);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [selectedWorker?.uuid]);

  // const getQuickBookEmployees = async () => {
  //   try {
  //     setisLoadingQuickBookEmployee(true);
  //     const response = await employeeApi.quickBooksEmployeeList();
  //     if (response && response.data) {
  //       setQuickBooksEmployees(
  //         response?.data?.map((x) => ({
  //           ...x,
  //           label: x.name,
  //           value: x.qb_id,
  //         })) || []
  //       );
  //     }
  //     setisLoadingQuickBookEmployee(false);
  //   } catch (err) {
  //     console.log("err", err);
  //     setisLoadingQuickBookEmployee(false);
  //   }
  // };

  const getTab = () => {
    if (
      isCustomHrmFormCategoryTabId(selectedTab) &&
      selectedWorker?.uuid
    ) {
      const category = customHrmCategoryByTabId.get(selectedTab);
      if (category) {
        return (
          <SubmittedFormView
            role='hrm'
            participantId={selectedWorker.id}
            supportPlansId={category.uuid}
            participantsUid={selectedWorker.uuid}
            title={category.name}
            parentSlug={category.slug}
            refreshProgressBar={getDocumentsListForProgressBar}
          />
        );
      }
    }

    switch (selectedTab) {
      case WORKER_PROFILE_TABS[0].id:
        return (
          <WorkerProfileDetails
            isUserGracePeriodOver={isUserGracePeriodOver}
            worker={selectedWorker}
            setSelectedTab={setSelectedTab}
            setRefreshPageForWorkerTimeSlot={setRefreshPageForWorkerTimeSlot}
            isWorkerAvailablity={isWorkerAvailablity}
          />
        );
      case WORKER_PROFILE_TABS[1].id:
        return (
          <WorkerDocument
            getDocumentsListForProgressBar={getDocumentsListForProgressBar}
            getFolderListForHRMLoading={getFolderListForHRMLoading}
            workerUUID={selectedWorker.uuid}
            worker={selectedWorker}
            documentList={documentList}
            isLoading={isLoading}
            documents={documents}
            setDocuments={setDocuments}
            getEmployeeDocuments={getEmployeeDocuments}
            openRequiredModal={openRequiredModalAgain}
            requiredDocumentslist={requiredDocuments}
            resetRequiredModalParentCheck={() => {
              setOpenRequiredModalAgain(false);
            }}
          />
        );
      // return <WorkerDocument workerUUID={selectedWorker.uuid} />;
      case WORKER_PROFILE_TABS[2].id:
        return (
          <WorkerSetting
            isUserGracePeriodOver={isUserGracePeriodOver}
            workerHourlyRateSettingRequest={workerHourlyRateSettingRequest}
            workerSettingCreateLoader={workerSettingCreateLoader}
            worker={selectedWorker}
            workerStatusUpdateLoader={workerStatusUpdateLoader}
            workerStatusUpdated={workerStatusUpdated}
            keyStrength={keyStrength}
            WorkerProfileDataRequest={WorkerProfileDataRequest}
            getDocumentsListForProgressBar={getDocumentsListForProgressBar}
            workerInfo={workerInfo}
            // quickBooksEmployees={quickBooksEmployees}
            // isLoadingQuickBookEmployee={isLoadingQuickBookEmployee}
            // syncQuickBooksEmployeesCall={syncQuickBooksEmployeesCall}
            // quickBooksBusinesses={quickBooksBusinesses}
            // isLoadingQuickBookBusiness={isLoadingQuickBookBusiness}
            // getQuickBookBusinesses={getQuickBookBusinesses}
            // getMyobBusiness={getMyobBusiness}
          />
        );
      case 5:
        return (
          <>
            {selectedWorker && selectedWorker.uuid ? (
              <SubmittedFormView
                role='hrm'
                parentSlug={PARENT_FORM_CATEGORY_SLUG.Compliance.SLUG}
                participantId={selectedWorker.id}
                supportPlansId={PARENT_FORM_CATEGORY_SLUG.Compliance.UUID}
                participantsUid={selectedWorker.uuid}
                title='Compliance'
                refreshProgressBar={getDocumentsListForProgressBar}
              />
            ) : (
              <CircularLoader
                classes='flex justify-center items-center'
                classloader='loader-l'
              />
            )}
          </>
        );
      case 6:
        return (
          <>
            <SubmittedFormView
              role='hrm'
              participantId={selectedWorker.id}
              supportPlansId={PARENT_FORM_CATEGORY_SLUG.Onboarding.UUID}
              participantsUid={selectedWorker.uuid}
              title='Onboarding'
              parentSlug={PARENT_FORM_CATEGORY_SLUG.Onboarding.SLUG}
              refreshProgressBar={getDocumentsListForProgressBar}
            />
          </>
        );
      case 8:
        return (
          <>
            <SubmittedFormView
              role='hrm'
              participantId={selectedWorker.id}
              supportPlansId={PARENT_FORM_CATEGORY_SLUG.Other.UUID}
              participantsUid={selectedWorker.uuid}
              title='Other'
              parentSlug={PARENT_FORM_CATEGORY_SLUG.Other.SLUG}
              refreshProgressBar={getDocumentsListForProgressBar}
            />
          </>
        );
      case 9:
        return (
          <>
            <SubmittedFormView
              role='hrm'
              supportPlansId={
                PARENT_FORM_CATEGORY_SLUG.Training_And_Development.UUID
              }
              participantsUid={selectedWorker?.uuid}
              title='Training And Development'
              participantId={selectedWorker?.id}
              parentSlug={
                PARENT_FORM_CATEGORY_SLUG.Training_And_Development.SLUG
              }
              refreshProgressBar={getDocumentsListForProgressBar}
            />
          </>
        );

      case 18:
        return (
          <>
            <SubmittedFormView
              role='hrm'
              supportPlansId={
                PARENT_FORM_CATEGORY_SLUG.Performance_Management.UUID
              }
              participantsUid={selectedWorker?.uuid}
              title='Performance Management'
              participantId={selectedWorker?.id}
              parentSlug={PARENT_FORM_CATEGORY_SLUG.Performance_Management.SLUG}
              refreshProgressBar={getDocumentsListForProgressBar}
            />
          </>
        );
      case 19:
        return (
          <>
            <SubmittedFormView
              role='hrm'
              participantId={selectedWorker.id}
              supportPlansId={PARENT_FORM_CATEGORY_SLUG.Exit_Transaction.UUID}
              participantsUid={selectedWorker.uuid}
              title={PARENT_FORM_CATEGORY_SLUG.Exit_Transaction.NAME}
              parentSlug={PARENT_FORM_CATEGORY_SLUG.Exit_Transaction.SLUG}
              refreshProgressBar={getDocumentsListForProgressBar}
            />
          </>
        );
      case 20:
        return (
          <ActivityTab entityType="employee_profile" entityId={selectedWorker.id} />
        );
      case 21:
        return (
          <WorkerWyzedSection
            worker={selectedWorker}
            isUserGracePeriodOver={isUserGracePeriodOver}
            onWorkerUpdated={() =>
              WorkerProfileDataRequest({
                profile_id: selectedWorker?.uuid,
              })
            }
          />
        );
      default:
        return (
          <WorkerProfileDetails
            isUserGracePeriodOver={isUserGracePeriodOver}
            worker={selectedWorker}
          />
        );
    }
  };

  return (
    <>
      <div className='w-full overflow-x-[clip]'>
        <SideBar classes='bg-gradient' />
        <div
          className={`${
            sideBarPadding ? "lg:pl-[4rem]" : "lg:pl-64"
          } duration-300`}
        >
          <main className='w-full pb-6'>
            {workerProfileDetailLoader ? (
              <div
                className={`flex flex-col w-full min-h-screen bg-gray-50 items-center justify-center`}
              >
                <CircularLoader
                  classes='flex justify-center items-center'
                  classloader='loader-l'
                />
              </div>
            ) : workerProfileError || !selectedWorker || Object.keys(selectedWorker).length === 0 ? (
              <div className='px-0 flex justify-center items-center min-h-screen bg-gray-50'>
                <div className="flex items-center bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-md shadow-sm">
                  <MdErrorOutline className="text-2xl mr-3 text-red-500"/>
                  <span className="font-semibold">Profile Not Found</span>
                </div>
              </div>
            ) : (
              <>
                <div className='sticky top-0 z-[20] bg-white'>
                  <WorkerProfileHeader
                    isUserGracePeriodOver={isUserGracePeriodOver}
                    selectedTab={selectedTab}
                    activeFormTab={activeFormTab}
                    workerInfo={selectedWorker}
                  />
                  <div className='w-full sm:px-6 lg:px-8'>
                    <div className='w-full print:hidden'>
                      <div className='relative px-2 bg-white flex justify-center print:hidden'>
                        <ComplianceProgressTooltip
                          profileContext='worker'
                          uuid={workerId}
                          progressBarDetails={progressbarData}
                          showList={!isFetchDocumentBarLoader && progressbarData.length > 0}
                          isLoading={isFetchDocumentBarLoader}
                          progressline={{
                            on_boarding_progress: progressbarData?.reduce(
                              (sum, item) => {
                                return item.submitted
                                  ? sum + Number(item.score || 0)
                                  : sum;
                              },
                              0
                            ),
                          }}
                        />
                      </div>
                    </div>
                    <div className='border-gray-200'>
                      <Breadcrumb
                        pages={HRM_BREADCRUMB(HRM_BREADCRUMB_PAGES.WORKER)}
                      />
                    </div>
                  </div>
                  <div className='sm:px-6 lg:px-8'>
                    <div className='print:hidden mt-4'>
                      <div className='bloc-tabs bg-gradient py-4 rounded-t-lg text-white'>
                        <Tabs
                          tabs={workerTabsVisible}
                          selectedTab={selectedTab}
                          handleChange={handleTab}
                          setChildId={setChildId}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className='sm:px-6 lg:px-8'>
                  <div className='bg-white shadow-lg rounded-b-lg h-fit'>
                    <div className='w-full max-w-full'>
                      <div className='min-w-0'>
                        <Suspense fallback={<TabContentFallback />}>
                          {getTab()}
                        </Suspense>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

const mapDispatchToProps = {
  workerHourlyRateSettingRequest: workerHourlyRateSettingUpdateRequest,
  WorkerProfileDataRequest: workerProfileDetailsFetchRequest,
  workerStatusUpdated: workerStatusUpdatedRequest,
};
const mapStateToProps = (state) => {
  const workerSettingCreateLoader = getWorkerHourlySettingLoader(state);
  const workerProfileDetailLoader = getWorkerProfileDetailLoader(state);
  const workerProfileError = getWorkerProfileError(state);
  const selectedWorker = getSelectedWorker(state);
  const workerStatusUpdateLoader = getWorkerStatusUpdateLoader(state);
  const getFolderListForHRMLoading = getFolderListForHRMLoadingSelector(state);
  const spRosteringSetting = getSpRosteringSetting(state);

  // export const getFolderListForHRMLoadingSelector = createSelector(
  //   cosStateData,
  //   (cos) => cos.get("removeFolderLoading") || false
  // );

  return {
    getFolderListForHRMLoading,
    spRosteringSetting,
    workerSettingCreateLoader,
    workerProfileDetailLoader,
    workerProfileError,
    selectedWorker,
    workerStatusUpdateLoader,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WorkerProfile);
